import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  List,
  ListItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { SessionPatientSchema } from '../store/hooks';

interface PatientSelectionPanelProps {
  patients: SessionPatientSchema[];
  /** 'all' commits every staged patient; 'specific' reveals the picker. */
  scope: 'all' | 'specific';
  onScopeChange: (scope: 'all' | 'specific') => void;
  /** Patient keys the operator has CHECKED (only meaningful in 'specific'). */
  selected: Set<string>;
  /** True when every listed patient is checked (drives the select-all box). */
  allChecked: boolean;
  onToggle: (patientKey: string) => void;
  onToggleAll: () => void;
  isLoading?: boolean;
}

/** "3 encounter · 3 note · 1 patient_coverage" — what's attached to a patient. */
function describeCounts(counts: SessionPatientSchema['counts']): string {
  const entries = Object.entries(counts ?? {});
  if (entries.length === 0) return 'no attached records';
  return entries.map(([type, n]) => `${n} ${type}`).join(' · ');
}

/**
 * Patient scope for a commit. Defaults to committing every staged patient; the
 * operator opts into "Specific patients" to reveal a checklist and commit only
 * the patients they check (each selection pulls in that patient's whole
 * dependency closure — the backend resolves it). Inclusion model: the list
 * starts empty, so committing one patient is one click.
 */
export function PatientSelectionPanel({
  patients,
  scope,
  onScopeChange,
  selected,
  allChecked,
  onToggle,
  onToggleAll,
  isLoading = false,
}: PatientSelectionPanelProps) {
  if (!isLoading && patients.length === 0) return null;

  const selectedCount = selected.size;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Patients
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={scope}
        onChange={(_e, next) => {
          if (next) onScopeChange(next as 'all' | 'specific');
        }}
        sx={{ mb: scope === 'specific' ? 1 : 0 }}
      >
        <ToggleButton value="all">All patients</ToggleButton>
        <ToggleButton value="specific">Specific patients</ToggleButton>
      </ToggleButtonGroup>

      {scope === 'all' && (
        <Typography variant="body2" color="text.secondary">
          Every staged patient in this batch will be committed.
        </Typography>
      )}

      {scope === 'specific' && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={allChecked}
                indeterminate={!allChecked && selectedCount > 0}
                disabled={patients.length === 0}
                onChange={onToggleAll}
                slotProps={{ input: { 'aria-label': 'Select all patients' } }}
              />
            }
            label={
              <Typography variant="body2">
                {selectedCount}/{patients.length} selected
              </Typography>
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Each checked patient is committed with everything attached to them;
            the rest are left staged.
          </Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
              {patients.map(p => (
                <ListItem key={p.patient_key} disableGutters sx={{ gap: 1 }}>
                  <Checkbox
                    size="small"
                    checked={selected.has(p.patient_key)}
                    onChange={() => onToggle(p.patient_key)}
                    slotProps={{
                      input: {
                        'aria-label': `Commit ${p.name || p.patient_key}`,
                      },
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {p.name || p.patient_key}
                      {!p.patient_staged && (
                        <Chip size="small" label="committed" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {describeCounts(p.counts)}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </>
      )}
    </Box>
  );
}
